import React, { useState, useEffect } from "react";
import { useDispatch } from "./redux/store";
import AutoCompleteInput from "./AutoCompleteInput";
import { updateAppointment } from "./redux/slices/appointmentSlice";

const CarPicker = ({
    vehicleId = 0,
    appointment,
    selectedVehicle,
    setSelectedVehicle,
    vehicleReset,
    setVehicleReset,
    setDeferredServices,
    setCarErrors,
}) => {
    const [allVehicles, setAllVehicles] = useState([]);
    const [allMakes, setAllMakes] = useState([]);
    const [allModels, setAllModels] = useState([]);
    const [allYears, setAllYears] = useState([]);
    const [make, setMake] = useState("");
    const [model, setModel] = useState("");
    const [year, setYear] = useState("");
    const [declinedMakes, setDeclinedMakes] = useState([]);
    const [declinedModels, setDeclinedModels] = useState([]);
    const [oldestYear, setOldestYear] = useState(null);
    const [errors, setErrors] = useState({ year: "", make: "", model: "" });
    const [, setNewVehicle] = useState({
        year: "",
        make: "",
        model: "",
        id: vehicleId,
    });

    const dispatch = useDispatch();

    useEffect(() => {
        const loadVehicles = async () => {
            // Fetch both classes and merge. Discontinued/commercial makes
            // (Scion, Eagle, Freightliner, ...) live only in UsedCar, so the
            // two responses must be combined — the previous code let whichever
            // response arrived last overwrite the other, so which makes showed
            // depended on network timing (and differed between environments).
            const [newCars, usedCars] = await Promise.all([
                fetchVehicles("NewCar"),
                fetchVehicles("UsedCar"),
            ]);
            setAllVehicles([...newCars, ...usedCars]);
        };
        loadVehicles();
    }, []);

    useEffect(() => {
        if (appointment?.shopData?.declinedMake) {
            setDeclinedMakes(
                appointment.shopData.declinedMake
                    .split(",")
                    .map((item) => item.trim())
            );
        }
        if (appointment?.shopData?.declinedModel) {
            setDeclinedModels(
                appointment.shopData.declinedModel
                    .split(",")
                    .map((item) => item.trim())
            );
        }
        if (appointment?.shopData?.oldestYearServicable) {
            setOldestYear(appointment.shopData.oldestYearServicable);
        }
    }, [
        appointment?.shopData?.declinedMake,
        appointment?.shopData?.declinedModel,
        appointment?.shopData?.oldestYearServicable,
    ]);

    useEffect(() => {
        const makes = Array.from(
            new Set(allVehicles.map((vehicle) => vehicle.make))
        );

        // Compare case-insensitively: the admin saves declined makes lowercase
        // ("ford") while KBB returns them title-cased ("Ford"). A case-sensitive
        // match left declined makes visible in the customer dropdown. Mirrors
        // the submit gate in stageValidation.ts.
        const declinedMakesLower = declinedMakes.map((m) => m.toLowerCase());
        const filteredMakes = (
            declinedMakesLower.length
                ? makes.filter((m) => !declinedMakesLower.includes(m.toLowerCase()))
                : makes
        ).sort((a, b) => a.localeCompare(b));

        // Sort as one set: merging NewCar + UsedCar otherwise yields each
        // class's alphabetized run concatenated, not a single sorted list.
        setAllMakes(filteredMakes);
    }, [allVehicles, declinedMakes]);

    useEffect(() => {
        const models = allVehicles
            .filter((vehicle) => vehicle.make === make)
            .map((vehicle) => vehicle.model);

        const uniqueModels = Array.from(new Set(models));

        const declinedModelsLower = declinedModels.map((m) => m.toLowerCase());
        const filteredModels = (
            declinedModelsLower.length
                ? uniqueModels.filter(
                      (m) => !declinedModelsLower.includes(m.toLowerCase())
                  )
                : uniqueModels
        ).sort((a, b) => a.localeCompare(b));

        setAllModels(filteredModels);
        
        // Clear model if it's not valid for the new make
        // The effect at lines 170-176 will handle dispatching to Redux
        if (model && !filteredModels.includes(model)) {
            setModel("");
        }
    }, [make, allVehicles, declinedModels, model]);

    useEffect(() => {
        const years = allVehicles
            .filter((vehicle) => vehicle.model === model)
            .flatMap((vehicle) => vehicle.years.map((y) => y.year));

        // Newest first, as one set (see makes note above re: merged classes).
        setAllYears(
            Array.from(new Set(years)).sort((a, b) => Number(b) - Number(a))
        );
    }, [model, allVehicles]);

    // useEffect(() => {
    //     if (appointment?.vehicle) {
    //         console.log("did I update?");
    //         if (Object.values(errors).every((error) => error === "")) {
    //             dispatch(
    //                 updateAppointment({ updates: { vehicle: newVehicle } })
    //             );
    //         }
    //     }
    // }, [errors, newVehicle, dispatch]);

    useEffect(() => {
        const updateErrors = {};

        // Check for declined makes and models
        if (
            declinedMakes
                .map((m) => m.toLowerCase())
                .includes(make.toLowerCase())
        ) {
            updateErrors.make = "We do not service your make of vehicle";
        }

        if (
            declinedModels
                .map((m) => m.toLowerCase())
                .includes(model.toLowerCase())
        ) {
            updateErrors.model = "We do not service your model of vehicle";
        }

        // Check the oldest serviceable year, but only set updateErrors.year if selectedVehicle.year doesn't meet criteria
        if (
            appointment?.shopData?.oldestYearServicable &&
            (selectedVehicle?.year < oldestYear ||
                (year !== "" && year < oldestYear))
        ) {
            updateErrors.year = `We only service vehicles manufactured during or after ${appointment.shopData.oldestYearServicable}`;
        }

        setErrors(updateErrors);
        setCarErrors(updateErrors);
    }, [
        selectedVehicle?.make,
        selectedVehicle?.model,
        selectedVehicle?.year,
        year,
        make,
        model,
        // appointment,
        // declinedMakes,
        // declinedModels,
        // oldestYear,
    ]);

    useEffect(() => {
        if (!vehicleReset) {
            handleVehicleReset();
        }

        handleNewVehicleSelections(year, make, model);
    }, [year, make, model]);

    const handleVehicleReset = () => {
        dispatch(
            updateAppointment({
                updates: {
                    vehicle: {
                        year: "",
                        make: "",
                        model: "",
                        deferredServices: [],
                        id: vehicleId,
                    },
                },
            })
        );
        setVehicleReset(true);
        setSelectedVehicle(null);
        setErrors({ year: "", make: "", model: "" });
        setCarErrors({ year: "", make: "", model: "" });
        setDeferredServices([]);
    };

    const handleNewVehicleSelections = (year, make, model) => {
        setNewVehicle((prev) => ({
          // ...prev,
          year: year,
          make: make,
          model: model,
          id: prev.id,
        }));

        dispatch(
            updateAppointment({
                updates: {
                    vehicle: {
                        ...appointment.vehicle,
                        crossRef: [],
                        year: year,
                        make: make,
                        model: model,
                    },
                },
            })
        );
    };

    const fetchVehicles = async (vehicleClass) => {
        const requestOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                operationName: "makemodel",
                variables: {
                    sortBy: "make",
                    vehicleClass: vehicleClass,
                    viewAll: true,
                },
                query: "query makemodel($sortBy: String, $vehicleClass: String, $viewAll: Boolean) { makemodel( sortBy: $sortBy vehicleClass: $vehicleClass viewAll: $viewAll ) { vehicles { make model years { year url __typename } __typename } validStartingLetters __typename }}",
            }),
            redirect: "follow",
        };

        try {
            const response = await fetch(
                `${import.meta.env.VITE_API_URL}/bookings/vehicleLookup`,
                requestOptions
            );
            const result = await response.json();
            return result?.data?.makemodel?.vehicles || [];
        } catch (error) {
            console.error(error);
            return [];
        }
    };

    return (
        <>
            {Object.values(errors).some((error) => error !== "") && (
                <div
                    className="mb-4 p-3 rounded-xl text-sm"
                    style={{
                        backgroundColor: "rgba(239, 68, 68, 0.1)",
                        color: "#ef4444",
                    }}
                >
                    <ul className="list-disc list-inside">
                        {Object.entries(errors).map(
                            ([key, value], index) =>
                                value && <li key={index}>{value}</li>
                        )}
                    </ul>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                    <label
                        htmlFor="Year"
                        className="text-sm font-semibold mb-1 block"
                        style={{ color: "#1f2937" }}
                    >
                        Year
                    </label>
                    <AutoCompleteInput
                        options={allYears}
                        id="Year"
                        setValue={setYear}
                        appointment={appointment}
                    />
                </div>

                <div>
                    <label
                        htmlFor="Make"
                        className="text-sm font-semibold mb-1 block"
                        style={{ color: "#1f2937" }}
                    >
                        Make
                    </label>
                    <AutoCompleteInput
                        options={allMakes}
                        id="Make"
                        setValue={setMake}
                        appointment={appointment}
                    />
                </div>

                <div>
                    <label
                        htmlFor="Model"
                        className="text-sm font-semibold mb-1 block"
                        style={{ color: "#1f2937" }}
                    >
                        Model
                    </label>
                    <AutoCompleteInput
                        options={allModels}
                        id="Model"
                        setValue={setModel}
                        appointment={appointment}
                    />
                </div>
            </div>
        </>
    );
};

export default CarPicker;
